import React, { useRef, useState, useEffect } from 'react';
import ImageService from '../images/imageService';
import ContentService from './ContentService';
import { marked } from 'marked';

const ContentHandler = () => {
  const [contentData, setContentData] = useState({
    title: '',
    category: '',
    extraData: '',
    fontSize: '18px',
    content: '',
    isMarkdown: false,
    images: '',
  });
  const [gallery, setGallery] = useState([]);
  const [slug, setSlug] = useState('');
  const editorRef = useRef(null);

  const handleInputChange = (key, value) => {
    setContentData((prev) => ({ ...prev, [key]: value }));
    if (key === 'title') {
      const newSlug = value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9\-]/g, '');
      setSlug(newSlug);
    }
  };

  const applyFormat = (tag) => {
    document.execCommand(tag, false, null);
  };

  const applyHeading = (level) => {
    document.execCommand('formatBlock', false, `<h${level}>`);
  };

  const handleImageDrop = async (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    const uploaded = [];

    for (let file of files) {
      const filename = `${slug}_${file.name}`;
      const path = `content`;
      const imageUrl = await ImageService.uploadImage(file, filename, path);
      uploaded.push(imageUrl);
    }

    const combined = [...gallery.map((img) => img.url), ...uploaded];
    setGallery(combined.map((url) => ({ url, selected: false })));
    setContentData((prev) => ({ ...prev, images: combined.join(',') }));
  };

  const handleImageSelection = (url) => {
    setGallery((prevGallery) =>
      prevGallery.map((img) => (img.url === url ? { ...img, selected: !img.selected } : img))
    );
  };

  const handleDeleteImages = async () => {
    const selected = gallery.filter((img) => img.selected);
    for (let img of selected) {
      const parts = img.url.split('/');
      const filename = parts.pop();
      const dir = parts.slice(-2, -1)[0];
      await ImageService.deleteImage(dir, filename);
    }
    const remaining = gallery.filter((img) => !img.selected).map((img) => img.url);
    setGallery(remaining.map((url) => ({ url, selected: false })));
    setContentData((prev) => ({ ...prev, images: remaining.join(',') }));
  };

  const handleSave = async () => {
    if (!contentData.title.trim()) {
      alert('Title is required.');
      return;
    }
    setContentData((prev) => ({
      ...prev,
      content: editorRef.current?.innerHTML || '',
    }));
    try {
      await ContentService.saveContent({ ...contentData, content: editorRef.current?.innerHTML || '' });
      alert('Content saved successfully.');
    } catch (err) {
      console.error(err);
      alert('Error saving content.');
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="bg-white p-6 rounded shadow">
        <h2 className="text-2xl font-bold mb-4">Content Creator</h2>

        <div className="mb-4">
          <label className="block font-semibold mb-1">Title</label>
          <input
            className="w-full border p-2 rounded"
            value={contentData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
          />
        </div>

        <div className="mb-4">
          <label className="block font-semibold mb-1">Category</label>
          <input
            className="w-full border p-2 rounded"
            value={contentData.category}
            onChange={(e) => handleInputChange('category', e.target.value)}
          />
        </div>

        <div className="mb-4">
          <label className="block font-semibold mb-1">Extra Data</label>
          <input
            className="w-full border p-2 rounded"
            value={contentData.extraData}
            onChange={(e) => handleInputChange('extraData', e.target.value)}
          />
        </div>

        <div className="mb-4">
          <label className="block font-semibold mb-1">Font Size</label>
          <input
            type="number"
            className="w-24 border p-2 rounded"
            value={contentData.fontSize}
            onChange={(e) => handleInputChange('fontSize', e.target.value)}
          />
        </div>

        {/* Formatting Toolbar */}
        <div className="flex gap-2 mb-4">
          <button onClick={() => applyFormat('bold')} className="px-3 py-1 border rounded">Bold</button>
          <button onClick={() => applyFormat('italic')} className="px-3 py-1 border rounded">Italic</button>
          <button onClick={() => applyFormat('underline')} className="px-3 py-1 border rounded">Underline</button>
          <button onClick={() => applyHeading(1)} className="px-3 py-1 border rounded">H1</button>
          <button onClick={() => applyHeading(2)} className="px-3 py-1 border rounded">H2</button>
          <button onClick={() => applyHeading(3)} className="px-3 py-1 border rounded">H3</button>
        </div>

        <div className="mb-4">
          <label className="block font-semibold mb-1">Content</label>
          <div
            ref={editorRef}
            className="border rounded p-4 min-h-[200px] bg-white text-black"
            contentEditable
            suppressContentEditableWarning
            style={{ fontSize: `${contentData.fontSize}px`, outline: 'none', whiteSpace: 'pre-wrap' }}
            dangerouslySetInnerHTML={{
              __html: contentData.content || '<p style="color:#888">Type your content here...</p>',
            }}
          ></div>
        </div>

        <div className="flex gap-4 mb-4">
          <button
            onClick={handleSave}
            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
          >
            Save
          </button>
          <button
            onClick={handleDeleteImages}
            className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
          >
            Delete Selected Images
          </button>
        </div>

        <div
          onDrop={handleImageDrop}
          onDragOver={(e) => e.preventDefault()}
          className="border-dashed border-2 border-gray-400 rounded p-4 text-center text-sm text-gray-600"
        >
          Drag and drop images here
        </div>

        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
          {gallery.map((img, idx) => (
            <div key={idx} className="relative group">
              <img src={img.url} className="w-full h-32 object-cover rounded" alt="" />
              <input
                type="checkbox"
                checked={img.selected}
                onChange={() => handleImageSelection(img.url)}
                className="absolute top-2 left-2"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ContentHandler;
